import { Button, Checkbox, Form, Input, Select, Space } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DeleteIcon from "../../../../../assets/icons/DeleteIcon";
import SelectDownIcon from "../../../../../assets/icons/SelectDownIcon";
import InfoItem from "../../../../../components/common/InfoItem";
import ConfirmModal from "../../../../../components/ui/ConfirmModal";
import Loader from "../../../../../components/ui/Loader";
import {
  useDeleteRoleMutation,
  useMakeCompanyAdminMutation,
  useMakeHRAdminMutation,
  useMakeSuperAdminMutation,
  useRoleStatusQuery,
} from "../../../../../features/emp-setting/employee/employeeApi";
import { useGetAllAssignProjectQuery } from "../../../../../features/portal/companyOnboard/companyOnboardApi";
import {
  useAssignUserToProjectRoleMutation,
  useGetAllPGAreaQuery,
  useGetAllPGRegionQuery,
  useGetAllPGTerritoriesQuery,
  useGetAllPGVendorQuery,
  useGetAllRoleQuery,
  useGetProjectsByUserQuery,
} from "../../../../../features/portal/Products/productsApi";
import { useGetSingleUserQuery } from "../../../../../features/portal/user/userApi";
import useError from "../../../../../hooks/useError";
import useModal from "../../../../../hooks/useModal";
import { toast } from "../../../../../lib/toast";
import { capitalize } from "../../../../../utils/string";
import { underscoreToCapitalizeString } from "../../../../../utils/utils";
import { replaceEditWithView } from "./utils";

export default function UserAccessPermission() {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { editId } = useParams();
  const { handleOpen, handleClose, open } = useModal<
    boolean | any,
    "CONFIRM_SUPER" | "CONFIRM_COMPANY" | "CONFIRM_HR"
  >();
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    null
  );
  const [selectedProjectService, setSelectedProjectService] = useState("");
  const [selectedRoleName, setSelectedRoleName] = useState("");
  const [selectRegion, setSelectRegion] = useState("");
  const [selectArea, setSelectArea] = useState("");
  const [selectCluster, setSelectCluster] = useState<string | null>(null);
  const backLink = replaceEditWithView(location.pathname + location.search);
  const {
    isLoading: isDetailsLoading,
    isError: isDetailsError,
    error: detailsError,
    data: detailsData,
  } = useGetSingleUserQuery(editId || "", {
    skip: !editId,
  });
  const companyId = detailsData?.data?.company_id;
  const {
    isError: isAllRoleError,
    error: allRoleError,
    data: allRoleData,
  } = useGetAllRoleQuery({
    params: {
      projectId: selectedProjectId,
    },
  });
  const [
    assignUserToProjectRole,
    {
      isError: isAssignRoleError,
      error: assignRoleError,
      isLoading: isAssignRoleLoading,
      isSuccess: isAssignRoleSuccess,
    },
  ] = useAssignUserToProjectRoleMutation();
  // console.log(companyId);
  const {
    isError: getAllAssignProjectIsError,
    error: getAllAssignProjectError,
    isLoading: getAllAssignProjectIsLoading,
    data: getAllAssignProject,
  } = useGetAllAssignProjectQuery(
    {
      id: Number(companyId),
    },
    {
      skip: !companyId,
    }
  );
  const { isError, error, isLoading, data } = useGetProjectsByUserQuery(
    {
      params: {
        userid: editId,
      },
    },
    {
      skip: !editId,
    }
  );
  const [
    makeCompanyAdmin,
    {
      isLoading: isCompanyAdminLoading,
      isError: isCompanyAdminError,
      error: companyAdminError,
      isSuccess: isCompanyAdminSuccess,
    },
  ] = useMakeCompanyAdminMutation();
  const [
    makeHRAdmin,
    {
      isLoading: isCompanyHRLoading,
      isError: isHRAdminError,
      error: hrAdminError,
      isSuccess: isHRAdminSuccess,
    },
  ] = useMakeHRAdminMutation();
  const [
    makeSuperAdmin,
    {
      isLoading: isSuperAdminLoading,
      isError: isSuperAdminError,
      error: superAdminError,
      isSuccess: isSuperAdminSuccess,
    },
  ] = useMakeSuperAdminMutation();
  const [
    deleteRole,
    {
      isLoading: isDeleteLoading,
      isError: isDeleteError,
      error: deleteError,
      isSuccess: isDeleteSuccess,
    },
  ] = useDeleteRoleMutation();
  const {
    isError: isRoleError,
    error: roleError,
    isLoading: isRoleLoading,
    data: roleData,
  } = useRoleStatusQuery(String(editId), {
    skip: !editId,
  });
  useError(isDetailsError, detailsError, "User details get fail");
  useError(isAllRoleError, allRoleError, "Get all role fail");
  useError(isRoleError, roleError, "Get role fail");
  useError(isCompanyAdminError, companyAdminError, "Make company admin fail");
  useError(isHRAdminError, hrAdminError, "Make HR admin fail");
  useError(isSuperAdminError, superAdminError, "Make super admin fail");
  useError(isDeleteError, deleteError, "Role delete fail");
  useError(
    getAllAssignProjectIsError,
    getAllAssignProjectError,
    "User details get fail"
  );
  useError(isError, error, "Permission get fail");
  useError(isAssignRoleError, assignRoleError, "Assign fail");

  const projectData = getAllAssignProject?.data?.projects;
  const allsRollData = allRoleData?.data?.data;
  const projectId = Form.useWatch("projectId", form);

  const {
    data: getAllPGRegionData,
    isLoading: getAllPGRegionIsLoading,
    isError: getAllPGRegionIsError,
    error: getAllPGRegionError,
  } = useGetAllPGRegionQuery({});
  const {
    data: getAllPGAreaData,
    isLoading: getAllPGAreaIsLoading,
    isError: getAllPGAreaIsError,
    error: getAllPGAreaError,
  } = useGetAllPGAreaQuery({
    params: {
      region: selectRegion,
    },
  });
  const {
    data: getAllPGTerritoriesData,
    isLoading: getAllPGTerritoriesIsLoading,
    isError: getAllPGTerritoriesIsError,
    error: getAllPGTerritoriesError,
  } = useGetAllPGTerritoriesQuery({
    params: {
      area: selectArea,
    },
  });

  const {
    data: getAllPGVendorData,
    isLoading: getAllPGVendorIsLoading,
    isError: getAllPGVendorIsError,
    error: getAllPGVendorError,
  } = useGetAllPGVendorQuery({});
  useError(getAllPGVendorIsError, getAllPGVendorError, "Get vendors data fail");
  useError(getAllPGRegionIsError, getAllPGRegionError, "Get region data fail");
  useError(getAllPGAreaIsError, getAllPGAreaError, "Get area data fail");
  useError(
    getAllPGTerritoriesIsError,
    getAllPGTerritoriesError,
    "Get territories data fail"
  );

  const roleId = Form.useWatch("roleId", form);
  const isSubmitDisabledRole = !projectId || !roleId;

  const onFinish = (values: any) => {
    const newData = {
      ...values,
      userId: Number(editId),
    };

    const newDataPG = {
      userId: Number(editId),
      roleId: values?.roleId,
      projectId: values?.projectId,
      pgUserData: {
        area: values?.area,
        email: values?.email,
        employeeId: values?.employeeId,
        nid: values?.nid,
        phoneNumber: values?.phoneNumber,
        region: values?.region,
        suppler: values?.suppler,
        teritory: values?.teritory,
        regionName:
          getAllPGRegionData?.data?.find((el) => el?._id === values?.region)
            ?.regionName || "",
        areaName:
          getAllPGAreaData?.data?.find((el) => el?._id === values?.area)
            ?.areaName || "",
        teritoryName:
          getAllPGTerritoriesData?.data?.find(
            (el) => el?._id === values?.teritory
          )?.teritoryName || "",
        lattitude: 23.7055748,
        longitude: 90.3621026,
        location: {
          type: "Point",
          coordinates: [90.3621026, 23.7055748],
        },
      },
    };

    if (selectedProjectService === "power_genie") {
      assignUserToProjectRole(newDataPG);
      // console.log("newData:", newDataPG);
    } else {
      assignUserToProjectRole(newData);
      // console.log("newData:", newData);
    }
  };

  useEffect(() => {
    if (isAssignRoleSuccess) {
      form.resetFields();
      // setFormType(null);
      toast("success", "Successfully user role assign");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAssignRoleSuccess]);
  useEffect(() => {
    if (isSuperAdminSuccess) {
      toast(
        "success",
        open?.data
          ? "Successfully make this user a super admin"
          : "Successfully remove super admin status from this user"
      );
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdminSuccess]);

  useEffect(() => {
    if (isCompanyAdminSuccess && open?.type === "CONFIRM_COMPANY") {
      toast(
        "success",
        open?.data
          ? "Successfully make this user a company admin"
          : "Successfully remove company admin status from this user"
      );
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompanyAdminSuccess]);

  useEffect(() => {
    if (isHRAdminSuccess && open?.type === "CONFIRM_HR") {
      toast(
        "success",
        open?.data
          ? "Successfully make this user a HR admin"
          : "Successfully remove HR admin status from this user"
      );
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHRAdminSuccess]);

  useEffect(() => {
    if (isDeleteSuccess) {
      toast("success", "Successfully role deleted");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeleteSuccess]);

  return (
    <div>
      {open?.type === "CONFIRM_SUPER" && (
        <ConfirmModal
          confirmTitle="Confirm"
          questionText={`Are you sure you want to ${
            open?.data
              ? "make this user a super admin"
              : "remove super admin status from this user"
          }?`}
          okText="Confirm"
          handleClose={handleClose}
          open={open?.open}
          okLoading={isSuperAdminLoading}
          handleOk={() => {
            makeSuperAdmin({
              body: { enable: open?.data },
              id: Number(editId),
            });
          }}
        />
      )}
      {open?.type === "CONFIRM_COMPANY" && (
        <ConfirmModal
          confirmTitle="Confirm"
          questionText={`Are you sure you want to ${
            open?.data
              ? "make this user a company admin"
              : "remove company admin status from this user"
          }? ${
            open?.data && roleData?.data?.isHRAdmin2
              ? "It will forcefully change HR admin to company admin."
              : ""
          }`}
          okText="Confirm"
          handleClose={handleClose}
          open={open?.open}
          okLoading={isCompanyAdminLoading}
          handleOk={() => {
            makeCompanyAdmin({
              body: { enable: open?.data },
              id: Number(editId),
            });
            if (open?.data && roleData?.data?.isHRAdmin2) {
              makeHRAdmin({
                body: { enable: false },
                id: Number(editId),
              });
            }
          }}
        />
      )}
      {open?.type === "CONFIRM_HR" && (
        <ConfirmModal
          confirmTitle="Confirm"
          questionText={`Are you sure you want to ${
            open?.data
              ? "make this user a HR admin"
              : "remove HR admin status from this user"
          }? ${
            open?.data && roleData?.data?.isCompanyAdmin
              ? "It will forcefully change company admin to HR admin."
              : ""
          }`}
          okText="Confirm"
          handleClose={handleClose}
          open={open?.open}
          okLoading={isCompanyHRLoading}
          handleOk={() => {
            makeHRAdmin({
              body: { enable: open?.data },
              id: Number(editId),
            });
            if (open?.data && roleData?.data?.isCompanyAdmin) {
              makeCompanyAdmin({
                body: { enable: false },
                id: Number(editId),
              });
            }
          }}
        />
      )}
      {open?.type === "CONFIRM" && (
        <ConfirmModal
          confirmTitle="Confirm"
          questionText={`Are you sure you want to delete this role?`}
          okText="Confirm"
          handleClose={handleClose}
          open={open?.open}
          okLoading={isDeleteLoading}
          handleOk={() => {
            deleteRole(open?.data);
          }}
        />
      )}
      {/* <InfoItem rootClassName="!py-3" className="space-x-1">
        <Checkbox
          id="supper_admin"
          checked={roleData?.data?.isSuperAdmin}
          disabled={isRoleLoading}
          onChange={(checked) => {
            handleOpen({
              type: "CONFIRM_SUPER",
              data: checked.target.checked,
            });
          }}
        />{" "}
        <label
          htmlFor="supper_admin"
          className="text-accent cursor-pointer select-none"
        >
          Assign as Super Admin
        </label>
      </InfoItem> */}
      <InfoItem rootClassName="!py-3" className="space-x-1">
        <Checkbox
          id="company_admin"
          checked={roleData?.data?.isCompanyAdmin}
          disabled={isRoleLoading}
          onChange={(checked) => {
            handleOpen({
              type: "CONFIRM_COMPANY",
              data: checked.target.checked,
            });
          }}
        />{" "}
        <label
          htmlFor="company_admin"
          className="text-accent cursor-pointer select-none"
        >
          Assign as Company Admin
        </label>
      </InfoItem>
      <InfoItem rootClassName="!py-3" className="space-x-1">
        <Checkbox
          id="hr_admin"
          checked={roleData?.data?.isHRAdmin2}
          disabled={isRoleLoading}
          onChange={(checked) => {
            handleOpen({
              type: "CONFIRM_HR",
              data: checked.target.checked,
            });
          }}
        />{" "}
        <label
          htmlFor="hr_admin"
          className="text-accent cursor-pointer select-none"
        >
          Assign as HR Admin
        </label>
      </InfoItem>
      {isLoading ? (
        <Loader />
      ) : (
        data?.data?.map((el, idx) => (
          <InfoItem
            rootClassName="!py-3 !grid !grid-cols-2 gap-2"
            titleClassName="!text-accent"
            className="space-x-1 flex justify-between items-center flex-wrap gap-1"
            title={el?.project?.name}
            key={idx}
          >
            {el?.role?.roleName === "edatco"
              ? "Admin"
              : el?.role?.roleName === "region"
              ? "Zonal"
              : el?.role?.roleName?.length <= 3
              ? el?.role?.roleName?.toUpperCase()
              : capitalize(el?.role?.roleName) || "N/A"}

            <Button
              className="action-button"
              size="small"
              icon={<DeleteIcon />}
              onClick={() => {
                handleOpen({
                  type: "CONFIRM",
                  data: {
                    userId: Number(editId),
                    projectId: el?.project?.id,
                    roleId: el?.role?.id,
                  },
                });
              }}
            />
          </InfoItem>
        ))
      )}
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        <div className="grid grid-cols-5 gap-4 mt-4">
          <Form.Item
            label="Select Product"
            name="projectId"
            rules={[{ required: true, message: "Please select product" }]}
            className="col-span-2"
          >
            <Select
              placeholder="Select Product"
              options={
                projectData?.map((item: any) => ({
                  label: item?.project_name,
                  value: item?.project_id,
                })) || []
              }
              onChange={(value) => {
                setSelectedProjectId(value);
                const selected = projectData?.find(
                  (p: any) => p.project_id === value
                );
                setSelectedProjectService(selected?.service_name || "");
                form.setFieldsValue({ roleId: undefined });
              }}
              className="w-full"
              suffixIcon={<SelectDownIcon />}
              style={{ width: "100%" }}
              allowClear
            />
          </Form.Item>

          <Form.Item
            label="Select Role"
            name="roleId"
            rules={[{ required: true, message: "Please select role" }]}
            className="col-span-2"
          >
            <Select
              placeholder="Select Role"
              options={
                allsRollData?.map((role: any) => {
                  let label = role?.roleName;

                  if (label === "edatco") {
                    label = "Admin";
                  } else if (label === "region") {
                    label = "Zonal";
                  } else if (label?.length <= 3) {
                    label = label?.toUpperCase();
                  }

                  return {
                    label: label?.includes("_")
                      ? underscoreToCapitalizeString(label)
                      : label,
                    value: role?.id,
                  };
                }) || []
              }
              onChange={(value) => {
                const selectedRole = allsRollData?.find(
                  (r: any) => r.id === value
                );
                setSelectedRoleName(selectedRole?.roleName || "");
              }}
              className="w-full"
              suffixIcon={<SelectDownIcon />}
              style={{ width: "100%" }}
              allowClear
              disabled={!projectId}
            />
          </Form.Item>

          {selectedProjectService === "power_genie" && (
            <>
              <Form.Item
                name="phoneNumber"
                label="Contact Number"
                rules={[
                  {
                    required: true,
                    message: "Please enter contact number",
                  },
                  {
                    validator: async (_: any, value: string) => {
                      if (!value) {
                        return Promise.resolve();
                      }

                      const phoneRegex = /^(\+88)?(01[3-9])[0-9]{8}$/;
                      if (!phoneRegex.test(value)) {
                        return Promise.reject(
                          new Error(
                            "Please enter a valid Bangladeshi contact number"
                          )
                        );
                      }
                    },
                  },
                ]}
              >
                <Input prefix={"BD "} size="large" placeholder="Mobile No:" />
              </Form.Item>

              <Form.Item
                name="employeeId"
                label="Employee ID"
                rules={[
                  {
                    required: true,
                    message: "Please enter employee ID",
                  },
                  {
                    validator: async (_: any, value: string) => {
                      if (!value) return Promise.resolve();

                      const trimmedValue = value.trim();

                      if (/^[-_]/.test(trimmedValue)) {
                        return Promise.reject(
                          new Error("Are not allowed start with '-' or '_'")
                        );
                      }

                      if (trimmedValue.includes(".")) {
                        return Promise.reject(
                          new Error("Decimal points are not allowed")
                        );
                      }

                      if (/\s/.test(trimmedValue)) {
                        return Promise.reject(
                          new Error("Spaces are not allowed")
                        );
                      }

                      if (!/^[a-zA-Z0-9_-]+$/.test(trimmedValue)) {
                        return Promise.reject(
                          new Error(
                            "Only alphabets, digits, hyphens (-), and underscores (_) are allowed."
                          )
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input size="large" placeholder="Enter Employee ID" />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email"
                rules={[
                  {
                    required: true,
                    message: "Please enter email",
                  },
                  {
                    validator: async (_: any, value: string) => {
                      if (!value) {
                        return Promise.resolve();
                      }

                      const emailRegex =
                        /^[a-zA-Z0-9]+([._%+-]?[a-zA-Z0-9]+)*@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/;
                      const consecutiveInvalidsRegex = /(\.\.|--|-\.)|(\.-)/;

                      if (
                        !emailRegex.test(value) ||
                        consecutiveInvalidsRegex.test(value)
                      ) {
                        return Promise.reject(
                          new Error(
                            "Please enter a valid email (no repeated or mixed special characters)"
                          )
                        );
                      }
                    },
                  },
                ]}
              >
                <Input size="large" placeholder="Enter email" disabled />
              </Form.Item>

              <Form.Item
                name="nid"
                label="NID"
                rules={[
                  {
                    required: true,
                    message: "Please enter NID",
                  },
                  {
                    validator: async (_: any, value: string) => {
                      if (!value) {
                        return Promise.resolve();
                      }

                      if (!/^\d+$/.test(value)) {
                        return Promise.reject(new Error("NID must be number"));
                      }

                      if (value.length < 10) {
                        return Promise.reject(
                          new Error("NID should be minimum 10 digits")
                        );
                      }

                      if (value.length > 20) {
                        return Promise.reject(
                          new Error("NID should not exceed 20 digits")
                        );
                      }
                    },
                  },
                ]}
              >
                <Input
                  className="w-full"
                  size="large"
                  placeholder="Enter NID"
                />
              </Form.Item>

              <Form.Item
                shouldUpdate={(prev, curr) => prev.roleId !== curr.roleId}
                noStyle
              >
                {({ getFieldValue }) => {
                  const roleId = getFieldValue("roleId");
                  const selectedRole = allsRollData?.find(
                    (r: any) => r.id === roleId
                  );
                  const isRequired =
                    selectedRole?.roleName === "Pg" ||
                    selectedRole?.roleName === "Vendor";

                  return (
                    <Form.Item
                      name="suppler"
                      label="Select Vendor"
                      rules={
                        isRequired
                          ? [
                              {
                                required: true,
                                message: "Please select vendor",
                              },
                            ]
                          : []
                      }
                    >
                      <Select
                        placeholder="Select Vendor"
                        size="large"
                        allowClear
                        showSearch
                        options={
                          getAllPGVendorData?.data?.map((vendor) => ({
                            label: vendor.supplierName,
                            value: vendor._id,
                          })) || []
                        }
                        filterOption={(input, option) =>
                          (option?.label as string)
                            ?.toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        onChange={(vendorId) => {
                          const vendor = getAllPGVendorData?.data?.find(
                            (v) => v._id === vendorId
                          );
                          if (vendor?.region?._id) {
                            setSelectCluster(vendor.region._id);
                            setSelectRegion(vendor.region._id);
                            form.setFieldValue("region", vendor.region._id);
                          } else {
                            setSelectCluster(null);
                            form.setFieldValue("region", undefined);
                          }
                        }}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>

              <Form.Item
                name="region"
                label="Select Cluster"
                rules={[
                  {
                    required: true,
                    message: "Please select cluster",
                  },
                ]}
              >
                <Select
                  placeholder="Select cluster"
                  size="large"
                  allowClear
                  showSearch
                  options={
                    getAllPGRegionData?.data?.map((region) => ({
                      label: capitalize(region.regionName),
                      value: region._id,
                    })) || []
                  }
                  filterOption={(input, option) =>
                    (option?.label as string)
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={(val) => {
                    setSelectRegion(val);
                    setSelectArea("");
                    form.setFieldValue("area", undefined);
                    form.setFieldValue("teritory", undefined);
                  }}
                  disabled={!!selectCluster}
                />
              </Form.Item>

              <Form.Item
                name="area"
                label="Select Zonal"
                rules={[
                  {
                    required: true,
                    message: "Please select zonal",
                  },
                ]}
              >
                <Select
                  placeholder="Select Zonal"
                  size="large"
                  allowClear
                  showSearch
                  options={getAllPGAreaData?.data?.map((area: any) => ({
                    label: capitalize(area.areaName),
                    value: area._id,
                  }))}
                  onChange={(val) => {
                    setSelectArea(val);
                    form.setFieldValue("teritory", undefined);
                  }}
                  disabled={!selectRegion}
                  loading={getAllPGAreaIsLoading}
                />
              </Form.Item>

              <Form.Item
                name="teritory"
                label="Select Territory"
                rules={[
                  {
                    required: true,
                    message: "Please select territory",
                  },
                ]}
              >
                <Select
                  placeholder="Select territory"
                  size="large"
                  allowClear
                  showSearch
                  options={getAllPGTerritoriesData?.data?.map(
                    (territory: any) => ({
                      label: capitalize(territory.teritoryName),
                      value: territory._id,
                    })
                  )}
                  disabled={!selectArea}
                  loading={getAllPGTerritoriesIsLoading}
                />
              </Form.Item>
            </>
          )}

          <Button
            className="modal-button max-w-[160px] pt-6 pb-6 mt-[26px]"
            type="primary"
            htmlType="submit"
            size="large"
            loading={isAssignRoleLoading}
            disabled={isSubmitDisabledRole}
          >
            Update
          </Button>
        </div>
      </Form>
      <Space className="w-full mt-2" size={12}>
        <Button
          className="normal-dark-button modal-button"
          onClick={() => navigate(backLink)}
          size="middle"
          //   disabled={isLoading}
        >
          Cancel
        </Button>
      </Space>
    </div>
  );
}
